import { create } from "zustand";

/**
 * The one place the app keeps "which project am I working in".
 *
 * Every section used to own a copy of this: Overview had ProjectPicker,
 * Agent Library had ProjectSelector and Execution History had ProjectFilter,
 * each with its own state and its own reads of localStorage. The picker now
 * lives once in the header (ProjectSwitcher) and every section reads from
 * here, so a choice made on one screen is still the choice on the next —
 * which is what makes switching project from inside an agent chat possible.
 *
 * Two selections are kept, because they answer different questions:
 *
 *   selectedProjectId — the working project. A real id, NO_PROJECT when the
 *     user deliberately runs outside a project (only offered to staff with
 *     free URL entry and to guests), or null before anything is chosen.
 *     Persisted, so a refresh lands in the same project.
 *
 *   historyProjectId — the Execution History filter only. null means "All
 *     Projects", which is its default on every visit, NO_PROJECT means runs
 *     that belong to no project. Deliberately not persisted: history opens on
 *     everything, as it always has.
 *
 * The store also handles:
 *   1. Old ID after the project was deleted: setProjects drops it.
 *   2. Different user logs in: clearProjects() on Sign Out wipes the old data.
 *   3. Sidebar on a page that doesn't load projects: UserMenu fetches them itself.
 */

// Sentinel for "no project", shared with the dropdown and the history filter.
// A string rather than null so it can be told apart from "nothing chosen yet",
// which is what null has always meant here.
export const NO_PROJECT = "none";

const useProjectStore = create((set, get) => ({
  projects: [],
  quota: null,
  selectedProjectId: localStorage.getItem("selectedProjectId") || null,
  historyProjectId: null, // null = All Projects

  // CHANGED: also drops a saved ID that no longer exists on the server
  setProjects: (projects) => {
    const { selectedProjectId } = get();
    const idIsStale =
      selectedProjectId &&
      selectedProjectId !== NO_PROJECT &&
      projects.length > 0 &&
      !projects.some((p) => p.id === selectedProjectId);

    if (idIsStale) {
      localStorage.removeItem("selectedProjectId");
      set({ projects, selectedProjectId: null });
    } else {
      set({ projects });
    }
  },

  // Project allowance (used / maxProjects / canCreate) as the projects API
  // reports it — the dropdown's "New Project (2/3)" footer reads this.
  setQuota: (quota) => set({ quota: quota || null }),

  setSelectedProjectId: (id) => {
    if (id) {
      localStorage.setItem("selectedProjectId", id);
    } else {
      localStorage.removeItem("selectedProjectId");
    }
    set({ selectedProjectId: id });
  },

  setHistoryProjectId: (id) => set({ historyProjectId: id }),

  // NEW — marks one project active, unmarks the rest
  setActiveProject: (id) =>
    set((state) => ({
      projects: state.projects.map((p) => ({ ...p, isDefault: p.id === id })),
    })),

  // NEW — wipes everything, call this on logout
  clearProjects: () => {
    localStorage.removeItem("selectedProjectId");
    set({
      projects: [],
      quota: null,
      selectedProjectId: null,
      historyProjectId: null,
    });
  },
}));

/**
 * The project id a run should actually be scoped to: a real id, or null when
 * the user is deliberately running outside a project. Callers that launch or
 * configure a run want this, never the raw selection.
 */
export function runProjectId(selectedProjectId) {
  return selectedProjectId === NO_PROJECT ? null : selectedProjectId || null;
}

export default useProjectStore;
